import { clone, find, findIndex, map, some } from 'lodash'
import Vue from 'vue'

import HTTP from '@/common/HTTP'

import { actionTypes, getTypes, mutationTypes } from './AuditsTypes';
import { actionTypes as globalActionTypes } from './GlobalTypes';

const answerPrototypes = {
  FREETEXT: {
    answer: '',
  },
  MULTI_SELECT: {
    selectedOptions: [],
  },
  DATETIME: {
    answer: '',
  }
}

function initialState() {
  return {
    audits: [],
    specificAudit: null,
    answers: [],
    pendingAnswers: [],
    currentSetDeficiencies: [],
    preparedDeficiencies: [],
  }
}

const getters = {
  [getTypes.GET_ANSWER](state) {
    return questionId => find(state.answers, { referencedQuestion: questionId }) || null
  },
  [getTypes.GET_PENDING_ANSWER](state) {
    return questionId => find(state.pendingAnswers, { referencedQuestion: questionId }) || null
  }
}

const mutations = {
  [mutationTypes.SET_AUDITS](state, item) {
    state.audits = item
  },

  [mutationTypes.SET_SPECIFIC_AUDIT](state, audit) {
    state.specificAudit = audit
  },

  [mutationTypes.SET_ANSWERS](state, answers) {
    state.answers = answers
  },

  [mutationTypes.SET_PENDING_ANSWERS](state, pendingAnswers) {
    state.pendingAnswers = pendingAnswers
  },

  [mutationTypes.UPDATE_PENDING_ANSWER](state, pendingAnswer) {
    Vue.set(state.pendingAnswers, findIndex(state.pendingAnswers,
      { referencedQuestion: pendingAnswer.referencedQuestion }), pendingAnswer)
  },

  // Deficiencies
  [mutationTypes.SET_CURRENT_SET_DEFICIENCIES](state, currentSetDeficiencies) {
    state.currentSetDeficiencies = currentSetDeficiencies
  },

  [mutationTypes.SET_PREPARED_DEFICIENCIES](state, preparedDeficiencies) {
    state.preparedDeficiencies = preparedDeficiencies
  },

  [mutationTypes.UPDATE_PREPARED_DEFICIENCY](state, preparedDeficiency) {
    Vue.set(state.preparedDeficiencies, findIndex(state.preparedDeficiencies,
      { id: preparedDeficiency.id }), preparedDeficiency)
  },

  [mutationTypes.UPDATE_DEFICIENCY_RESULT](state, { preparedDeficiencyId, deficiencyResult }) {
    const preparedDeficiency = find(state.preparedDeficiencies, { id: preparedDeficiencyId })
    Vue.set(state.preparedDeficiencies, findIndex(state.preparedDeficiencies,
      { id: preparedDeficiency.id }), { ...preparedDeficiency, deficiencyResult })
  }
}

const actions = {
  [actionTypes.FETCH_AUDITS]({ commit }) {
    return HTTP.get('/audits?sort=created,desc').then(({ data: { content } }) => {
      commit(mutationTypes.SET_AUDITS, content)
    })
  },

  [actionTypes.FETCH_AUDIT]({ commit }, id) {
    return HTTP.get(`/audits/${id}`).then(({ data }) => {
      commit(mutationTypes.SET_SPECIFIC_AUDIT, data)
    })
  },

  async [actionTypes.CREATE_AUDIT]({ commit, dispatch }, audit) {
    await HTTP.post('/audits', audit)

    return dispatch(actionTypes.FETCH_AUDITS)
  },

  [actionTypes.FETCH_ANSWERS]({ commit, disptach }, auditId) {
    return HTTP.get(`/audits/${auditId}/answers`).then(({ data }) => {
      commit(mutationTypes.SET_ANSWERS, data)
    })
  },

  [actionTypes.DELETE_ANSWER]({ dispatch }, { auditId, answer }) {
    return HTTP.delete(`/audits/${auditId}/answers/${answer.id}`).then(() => {
      dispatch(actionTypes.FETCH_ANSWERS, auditId);
    })
  },

  [actionTypes.PREPARE_PENDING_ANSWERS]({ commit, state }, questionSheet) {
    const pendingAnswers = map(questionSheet.questions,
      question => ({
        title: state.specificAudit.name,
        description: state.specificAudit.description,
        referencedQuestion: question.id,
        ...answerPrototypes[question.type]
      }))
    commit(mutationTypes.SET_PENDING_ANSWERS, pendingAnswers)
  },

  [actionTypes.SAVE_PENDING_ANSWERS]({ state, dispatch }) {
    // needs to be clean up
    Promise.all(state.pendingAnswers.filter(a => ((a.selectedOptions && a.selectedOptions.length > 0) || a.answer) &&
      !some(state.answers, { referencedQuestion: a.referencedQuestion }))
      .map((answer) => {
        const payload = clone(answer)
        if (payload.selectedOptions) {
          payload.selectedOptions = payload.selectedOptions.map(option => ({
            optionId: option.id,
            title: option.title,
            description: option.description,
            cps: option.cps
          }))
        }
        return HTTP.post(`/audits/${state.specificAudit.id}/answers`, payload)
      })).then(() => {
        dispatch(actionTypes.FETCH_AUDIT, state.specificAudit.id);
        dispatch(actionTypes.FETCH_ANSWERS, state.specificAudit.id);
      })
  },

  // Deficiencies
  [actionTypes.FETCH_CURRENT_SET_DEFICIENCIES]({ commit }, { auditId, setId }) {
    return HTTP.get(`/audits/${auditId}/preparable-deficiencies-sets/${setId}/preparable-deficiencies`)
      .then(({ data }) => {
        commit(mutationTypes.SET_CURRENT_SET_DEFICIENCIES, data)
      })
  },

  [actionTypes.FETCH_PREPARED_DEFICIENCIES]({ commit }, auditId) {
    return HTTP.get(`/audits/${auditId}/prepared-deficiencies`).then(({ data }) => {
      commit(mutationTypes.SET_PREPARED_DEFICIENCIES, data)
    })
  },

  async [actionTypes.CREATE_PREPARED_DEFICIENCY]({ commit, dispatch }, { auditId, deficiency }) {
    await HTTP.post(`/audits/${auditId}/prepared-deficiencies`, {
      name: deficiency.part,
      description: deficiency.description,
      deficiency: deficiency.id
    })

    await dispatch(actionTypes.FETCH_PREPARED_DEFICIENCIES, auditId)
  },

  async [actionTypes.UPDATE_PREPARED_DEFICIENCY]({ commit, dispatch }, { auditId, preparedDeficiency }) {
    await HTTP.patch(`/audits/${auditId}/prepared-deficiencies/${preparedDeficiency.id}`, {
      name: preparedDeficiency.name,
      description: preparedDeficiency.description,
    })

    await dispatch(actionTypes.FETCH_PREPARED_DEFICIENCIES, auditId)
  },

  async [actionTypes.DELETE_PREPARED_DEFICIENCY]({ dispatch }, { auditId, preparedDeficiency }) {
    try {
      await HTTP.delete(`/audits/${auditId}/prepared-deficiencies/${preparedDeficiency.id}`)
      await dispatch(actionTypes.FETCH_PREPARED_DEFICIENCIES, auditId)
    } catch (e) {
      dispatch(globalActionTypes.SHOW_SNACKBAR, {
        message: 'Bereits bearbeitete Mängel können nicht gelöscht werden',
        color: 'error'
      }, { root: true })
    }
  },

  async [actionTypes.SAVE_PREPARED_DEFICIENCY_PROOF]({ dispatch }, { auditId, preparedDeficiencyId, file }) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('meta', JSON.stringify(
      { description: '', hasTimestamp: true, type: 'IMAGE' }))
    await HTTP.post(`/audits/${auditId}/prepared-deficiencies/${preparedDeficiencyId}/proofs`, formData, {
      headers: { Accept: 'application/json', 'Content-Type': 'multipart/form-data' }
    })
    await dispatch(actionTypes.FETCH_PREPARED_DEFICIENCIES, auditId)
  },

  async [actionTypes.SAVE_DEFICIENCY_RESULT]({ commit },
    { auditId, preparedDeficiencyId, deficiencyResult }) {
    const updatedDeficiencyResult = (await HTTP.put(`/audits/${auditId}/prepared-deficiencies/`
      + `${preparedDeficiencyId}/result`, {
        positivelyHandled: deficiencyResult.positivelyHandled,
        description: deficiencyResult.description,
      })).data
    commit(mutationTypes.UPDATE_DEFICIENCY_RESULT, { preparedDeficiencyId, deficiencyResult: updatedDeficiencyResult })
  },

  async [actionTypes.SAVE_DEFICIENCY_RESULT_PROOF]({ dispatch }, { auditId, preparedDeficiencyId, file }) {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('meta', JSON.stringify(
      { description: '', hasTimestamp: true, type: 'IMAGE' }))
    await HTTP.post(`/audits/${auditId}/prepared-deficiencies/${preparedDeficiencyId}/result/proofs`,
      formData, {
        headers: { Accept: 'application/json', 'Content-Type': 'multipart/form-data' }
      })
    await dispatch(actionTypes.FETCH_PREPARED_DEFICIENCIES, auditId)
  },

}

export default {
  namespaced: true,
  state: initialState,
  actions,
  mutations,
  getters
}



// WEBPACK FOOTER //
// ./src/store/modules/Audits.js